import { i18n } from "../../../../internationalize/i18n.service"
import { TimeRange } from "../../../models/historical-charts/time-range.enum";
import styleCSS from "./chart-time-range.style.scss?inline"

const TIME_RANGE_KEYS: Record<TimeRange, string> = {
  [TimeRange.All]: "timeRangeAll",
  [TimeRange.Year]: "timeRange1Y",
  [TimeRange.Quarter]: "timeRange3M",
  [TimeRange.Month]: "timeRange1M",
}

export class TimeRangeChangeRequestEvent extends Event {
  static readonly EVENT_NAME = "timeChangeRequest"
  constructor(type: string, public timeRange: TimeRange) { super(type) }
}

export class TimeRangeComponent extends HTMLElement {
  static readonly ATTRIBUTE_AUTH = "full-access"
  static readonly SELECTED_ATTR = "selected-range"

  static get observedAttributes() { return [TimeRangeComponent.ATTRIBUTE_AUTH, this.SELECTED_ATTR] }

  private readonly _ranges: Array<{ value: TimeRange; label: string }> = [
    { value: TimeRange.All, label: "All" },
    { value: TimeRange.Year, label: "1Y" },
    { value: TimeRange.Quarter, label: "3M" },
    { value: TimeRange.Month, label: "1M" }
  ]
  private _timeRangeButtons!: Array<HTMLButtonElement>
  private _shadowRoot: ShadowRoot

  elemIndex = (e: HTMLElement): number => +(e.dataset.index!)
  elemRange = (e: HTMLElement): TimeRange => this._ranges[this.elemIndex(e)].value

  clickHandler = (e: MouseEvent) => {
    const range = this.elemRange(e.target as HTMLElement)
    window.tbwServices.analyticsService.track("graph_date_range_click", { time_range: range })
    this.dispatchEvent(new TimeRangeChangeRequestEvent(TimeRangeChangeRequestEvent.EVENT_NAME, range))
  }

  constructor() {
    super()
    this._shadowRoot = this.attachShadow({ mode: 'open' })
    this._shadowRoot.innerHTML = `
      <style>${styleCSS}</style>
    `
  }

  connectedCallback() {
    const rangeButtonMapFn = (rangeDesc: { value: TimeRange, label: string }, index: number): HTMLButtonElement => {
      const btn: HTMLButtonElement = document.createElement('button')
      btn.dataset.index = index.toString()
      btn.innerText = i18n.t(TIME_RANGE_KEYS[rangeDesc.value]) ?? rangeDesc.label
      return btn
    }
    this._timeRangeButtons = this._ranges.map(rangeButtonMapFn)
    this._timeRangeButtons.forEach(b => this._shadowRoot.appendChild(b))
    this._timeRangeButtons.forEach(b => this.enableButton(b))
  }

  disconnectedCallback() {
    this._timeRangeButtons.forEach(b => this.disableButton(b))
  }

  enableButton(b: HTMLButtonElement): HTMLButtonElement {
    b.classList.remove('disabled')
    b.addEventListener('click', this.clickHandler)
    return b
  }

  disableButton(b: HTMLButtonElement): HTMLButtonElement {
    b.classList.add('disabled')
    b.removeEventListener('click', this.clickHandler)
    return b
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) { return }
    if (name === TimeRangeComponent.ATTRIBUTE_AUTH) { return this.handleFullAccessChanged(newValue) }
    if (name === TimeRangeComponent.SELECTED_ATTR) { return this.handleSelectedRangeChanged(oldValue, newValue) }
  }

  handleSelectedRangeChanged(_oldValue: string, newValue: string): void {
    const deselectedButton = this._timeRangeButtons.find(b => this._ranges[+b.dataset.index!].value === (_oldValue as TimeRange))
    const selectedButton = this._timeRangeButtons.find(b => this._ranges[+b.dataset.index!].value === (newValue as TimeRange))
    deselectedButton?.classList.remove('selected')
    selectedButton?.classList.add('selected')
  }

  handleFullAccessChanged(newValue: string): void {
    const newFullAccess: boolean = newValue === 'true'
    if (newFullAccess) {
      this._timeRangeButtons.forEach(this.enableButton.bind(this))
    } else {
      this._timeRangeButtons.forEach(this.disableButton.bind(this))
    }
  }
}
